import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { getAdminPath, isAdminPathConfigured } from '@/config/admin';
import axios from 'axios';

// Landing page
const LandingView = () => import('@/views/public/LandingView.vue');

// Auth views
const LoginView = () => import('@/views/auth/LoginView.vue');
const RegisterView = () => import('@/views/auth/RegisterView.vue');
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue');
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView.vue');
const AuthCallbackView = () => import('@/views/auth/AuthCallbackView.vue');

// Client dashboard views
const DashboardLayout = () => import('@/components/layouts/DashboardLayout.vue');
const EditorLayout = () => import('@/components/layouts/EditorLayout.vue');
const DashboardView = () => import('@/views/dashboard/DashboardView.vue');
const SignView = () => import('@/views/dashboard/SignView.vue');
const DocumentsView = () => import('@/views/dashboard/DocumentsView.vue');
const TemplatesView = () => import('@/views/dashboard/TemplatesView.vue');
const DocumentCreatorView = () => import('@/views/dashboard/DocumentCreatorView.vue');
const FieldPlacementView = () => import('@/views/dashboard/FieldPlacementView.vue');
const PdfEditorView = () => import('@/views/dashboard/PdfEditorView.vue');
const TeamView = () => import('@/views/dashboard/TeamView.vue');
const ContactsView = () => import('@/views/dashboard/ContactsView.vue');
const SettingsView = () => import('@/views/dashboard/SettingsView.vue');
const BillingView = () => import('@/views/dashboard/BillingView.vue');

// Public views
const PublicSignView = () => import('@/views/public/PublicSignView.vue');
const AcceptInviteView = () => import('@/views/public/AcceptInviteView.vue');
const SealVerifyView = () => import('@/views/public/SealVerifyView.vue');

// Admin views
const AdminLayout = () => import('@/components/layouts/AdminLayout.vue');
const AdminDashboard = () => import('@/views/admin/AdminDashboard.vue');
const ClientsListView = () => import('@/views/admin/ClientsListView.vue');
const ClientDetailView = () => import('@/views/admin/ClientDetailView.vue');
const UsersListView = () => import('@/views/admin/UsersListView.vue');
const UserDetailView = () => import('@/views/admin/UserDetailView.vue');
const AdminSettingsView = () => import('@/views/admin/SettingsView.vue');
const AdminIntegrationsView = () => import('@/views/admin/IntegrationsView.vue');
const AdminPlansView = () => import('@/views/admin/PlansView.vue');
const AdminPromoCodesView = () => import('@/views/admin/PromoCodesView.vue');
const AdminBillingOverview = () => import('@/views/admin/BillingOverviewView.vue');
const AdminTemplatesView = () => import('@/views/admin/TemplatesView.vue');
const AdminDocumentCreatorView = () => import('@/views/admin/AdminDocumentCreatorView.vue');
const AdminLandingPageView = () => import('@/views/admin/LandingPageView.vue');
const AdminPagesView = () => import('@/views/admin/PagesView.vue');
const AdminPageEditorView = () => import('@/views/admin/PageEditorView.vue');
const AdminPromptsView = () => import('@/views/admin/AdminPromptsView.vue');

// Public pages
const PageView = () => import('@/views/public/PageView.vue');
const PricingView = () => import('@/views/public/PricingView.vue');
const PromptingView = () => import('@/views/public/PromptingView.vue');

// Cache for landing page enabled status
let landingPageEnabled: boolean | null = null;

// Function to check if landing page is enabled
async function checkLandingPageEnabled(): Promise<boolean> {
  if (landingPageEnabled !== null) {
    return landingPageEnabled;
  }

  try {
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    const response = await axios.get(`${apiUrl}/auth/landing-settings`);
    const enabled = response.data.data?.landing?.enabled === true;
    landingPageEnabled = enabled;
    return enabled;
  } catch {
    landingPageEnabled = false;
    return false;
  }
}

// Export function to reset cache when admin toggles landing page
export function resetLandingPageCache() {
  landingPageEnabled = null;
}

// Static routes (admin routes are added dynamically at runtime)
const routes: RouteRecordRaw[] = [
  // Landing page (public, but redirects if not enabled)
  {
    path: '/',
    name: 'landing',
    component: LandingView,
    meta: { landingPage: true },
  },

  // Public signing route (no auth required)
  {
    path: '/sign-public',
    name: 'public-sign',
    component: PublicSignView,
    meta: { public: true },
  },

  // Accept team invitation (no auth required)
  {
    path: '/accept-invite',
    name: 'accept-invite',
    component: AcceptInviteView,
    meta: { public: true },
  },

  // Electronic seal verification (public). Two entry points share
  // the same view: /verify-seal/:id (badge link) and /verify
  // (drop-zone for callers holding only the PDF).
  {
    path: '/verify-seal/:id',
    name: 'verify-seal-by-id',
    component: SealVerifyView,
    meta: { public: true },
  },
  {
    path: '/verify',
    name: 'verify-seal-upload',
    component: SealVerifyView,
    meta: { public: true },
  },

  // Direct routes for privacy-policy and terms (Google Auth verification requires these)
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: PageView,
    meta: { public: true },
    props: { slug: 'privacy-policy' },
  },
  {
    path: '/terms',
    name: 'terms',
    component: PageView,
    meta: { public: true },
    props: { slug: 'terms' },
  },

  // Public page view (Terms, Privacy, etc.)
  {
    path: '/page/:slug',
    name: 'page',
    component: PageView,
    meta: { public: true },
  },

  // Pricing page
  {
    path: '/pricing',
    name: 'pricing',
    component: PricingView,
    meta: { public: true },
  },

  // Prompting page
  {
    path: '/prompting',
    name: 'prompting',
    component: PromptingView,
    meta: { public: true },
  },

  // Auth routes
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { guestOnly: true },
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: { guestOnly: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: { guestOnly: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { guestOnly: true },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: AuthCallbackView,
    meta: { public: true },
  },

  // Plan selection (shown after registration for users with no plan)
  {
    path: '/select-plan',
    name: 'select-plan',
    component: () => import('@/views/auth/SelectPlanView.vue'),
    meta: { requiresAuth: true, roles: ['client'] },
  },

  // Client dashboard routes
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresAuth: true, roles: ['client', 'client_team'] },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: DashboardView,
      },
      {
        path: 'sign',
        name: 'sign',
        component: SignView,
      },
      {
        path: 'sign/:id',
        name: 'sign-document',
        component: SignView,
      },
      {
        path: 'documents',
        name: 'documents',
        component: DocumentsView,
      },
      {
        path: 'documents/:id',
        name: 'document-detail',
        component: DocumentsView,
      },
      {
        path: 'templates',
        name: 'templates',
        component: TemplatesView,
      },
      {
        path: 'editor',
        name: 'editor',
        component: DocumentCreatorView,
      },
      {
        path: 'field-placement',
        name: 'field-placement',
        component: FieldPlacementView,
      },
      {
        path: 'pdf-editor',
        name: 'pdf-editor',
        component: PdfEditorView,
      },
      {
        path: 'tools',
        name: 'tools',
        component: () => import('@/views/dashboard/tools/ToolsLandingView.vue'),
      },
      {
        path: 'tools/pdf-editor',
        name: 'tools-pdf-editor',
        component: () => import('@/views/dashboard/tools/PdfEditorToolView.vue'),
      },
      {
        path: 'tools/image-to-pdf',
        name: 'tools-image-to-pdf',
        component: () => import('@/views/dashboard/tools/ImageToPdfView.vue'),
      },
      {
        path: 'tools/pdf-to-jpg',
        name: 'tools-pdf-to-jpg',
        component: () => import('@/views/dashboard/tools/PdfToJpgView.vue'),
      },
      {
        path: 'tools/pdf-word',
        name: 'tools-pdf-word',
        component: () => import('@/views/dashboard/tools/PdfWordView.vue'),
      },
      {
        path: 'tools/pdf-excel',
        name: 'tools-pdf-excel',
        component: () => import('@/views/dashboard/tools/PdfExcelView.vue'),
      },
      {
        path: 'team',
        name: 'team',
        component: TeamView,
      },
      {
        path: 'contacts',
        name: 'contacts',
        component: ContactsView,
      },
      {
        path: 'contacts/:id',
        name: 'contact-detail',
        component: () => import('@/components/contacts/ContactDetailPage.vue'),
      },
      {
        path: 'settings',
        name: 'settings',
        component: SettingsView,
      },
      {
        path: 'billing',
        name: 'billing',
        component: BillingView,
      },
      {
        path: 'billing/success',
        name: 'billing-success',
        component: BillingView,
      },
    ],
  },

  // Full-page PDF editor (no sidebar/header)
  {
    path: '/pdf-editor/:id',
    component: EditorLayout,
    meta: { requiresAuth: true, roles: ['client', 'client_team'] },
    children: [
      {
        path: '',
        name: 'pdf-editor-document',
        component: PdfEditorView,
      },
    ],
  },

  // Full-page Document Creator (no sidebar/header)
  {
    path: '/document-creator',
    component: EditorLayout,
    meta: { requiresAuth: true, roles: ['client', 'client_team'] },
    children: [
      {
        path: '',
        name: 'document-creator',
        component: DocumentCreatorView,
      },
      {
        path: ':id',
        name: 'document-creator-edit',
        component: DocumentCreatorView,
      },
    ],
  },

  // NOTE: Admin routes and catch-all are added dynamically in loadAdminRoutes()
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    // Handle anchor links for landing page
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

// Dynamic admin route loading - fetches admin path from backend at runtime
let adminRoutesLoaded = false;

async function loadAdminRoutes(): Promise<void> {
  if (adminRoutesLoaded) return;

  // SECURITY: only register admin routes if VITE_ADMIN_PATH was baked in
  // at build time with a strong, non-trivial slug. Otherwise we skip the
  // admin route graph entirely — visiting /admin/login (or any other
  // guessable admin URL) just falls through to the catch-all 404 redirect.
  if (isAdminPathConfigured()) {
    const adminPath = getAdminPath();

    router.addRoute({
      path: `/${adminPath}/login`,
      name: 'admin-login',
      component: () => import('@/views/admin/AdminLoginView.vue'),
      meta: { adminAuth: true },
    });

    router.addRoute({
      path: `/${adminPath}`,
      component: AdminLayout,
      meta: { requiresAuth: true, roles: ['super_admin', 'admin'] },
      children: [
        { path: '', name: 'admin-dashboard', component: AdminDashboard },
        { path: 'clients', name: 'admin-clients', component: ClientsListView },
        { path: 'clients/:id', name: 'admin-client-detail', component: ClientDetailView },
        { path: 'users', name: 'admin-users', component: UsersListView },
        { path: 'users/:id', name: 'admin-user-detail', component: UserDetailView },
        { path: 'integrations', name: 'admin-integrations', component: AdminIntegrationsView },
        { path: 'settings', name: 'admin-settings', component: AdminSettingsView },
        { path: 'landing-page', name: 'admin-landing-page', component: AdminLandingPageView },
        { path: 'pages', name: 'admin-pages', component: AdminPagesView },
        { path: 'pages/:id', name: 'admin-page-editor', component: AdminPageEditorView },
        { path: 'prompts', name: 'admin-prompts', component: AdminPromptsView },
        { path: 'plans', name: 'admin-plans', component: AdminPlansView },
        { path: 'promo-codes', name: 'admin-promo-codes', component: AdminPromoCodesView },
        { path: 'billing', name: 'admin-billing', component: AdminBillingOverview },
        { path: 'templates', name: 'admin-templates', component: AdminTemplatesView },
        { path: 'document-creator', name: 'admin-document-creator', component: AdminDocumentCreatorView },
        { path: 'document-creator/:id', name: 'admin-document-creator-edit', component: AdminDocumentCreatorView },
        { path: 'profile', name: 'admin-profile', component: () => import('@/views/admin/AdminProfileView.vue') },
        { path: 'admin-team', name: 'admin-team', component: () => import('@/views/admin/AdminTeamView.vue') },
      ],
    });
  }

  // Add catch-all LAST (after admin routes so they take priority). Anything
  // that wasn't matched — including /admin, /admin/login, and other decoy
  // paths when the admin slug is not configured — bounces to the public
  // login page instead of revealing that an admin area exists.
  router.addRoute({
    path: '/:pathMatch(.*)*',
    redirect: '/login',
  });

  adminRoutesLoaded = true;
}

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  // Load admin routes dynamically on first navigation
  if (!adminRoutesLoaded) {
    await loadAdminRoutes();
    // Re-process this navigation now that admin routes are registered
    return next(to.fullPath);
  }

  // Allow public routes without any auth checks
  if (to.meta.public) {
    return next();
  }

  const authStore = useAuthStore();
  const adminPath = getAdminPath();
  // Only consider a URL an "admin route" when an admin path is actually
  // configured. If it isn't, the admin route tree was never registered,
  // so this check should never match anything.
  const isAdminRoute = isAdminPathConfigured() && to.path.startsWith(`/${adminPath}`);

  // Wait for auth to initialize if we have a token but no user data yet
  if (authStore.isAuthenticated && !authStore.user && !authStore.initialized) {
    await authStore.initAuth();
  }

  // Handle admin login page
  if (to.meta.adminAuth) {
    // If already authenticated as an admin-level user, go to admin dashboard
    const role = authStore.user?.role;
    if (authStore.isAuthenticated && (role === 'super_admin' || role === 'admin')) {
      return next({ name: 'admin-dashboard' });
    }
    return next();
  }

  // Handle landing page route
  if (to.meta.landingPage) {
    // If user is already authenticated, redirect to their dashboard
    if (authStore.isAuthenticated && authStore.user) {
      if (authStore.user.role === 'super_admin' || authStore.user.role === 'admin') {
        return next({ name: 'admin-dashboard' });
      }
      return next({ name: 'dashboard' });
    }

    // Check if landing page is enabled
    const isLandingEnabled = await checkLandingPageEnabled();
    if (!isLandingEnabled) {
      return next({ name: 'login' });
    }

    return next();
  }

  // Check if route requires auth
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Admin routes redirect to admin login, client routes to client login
      if (isAdminRoute) {
        return next({ name: 'admin-login' });
      }
      return next({ name: 'login', query: { redirect: to.fullPath } });
    }

    // Check role requirements (only if user data is loaded)
    const allowedRoles = to.meta.roles as string[] | undefined;
    if (allowedRoles && authStore.user && !allowedRoles.includes(authStore.user.role)) {
      // Redirect based on role
      if (authStore.user.role === 'super_admin' || authStore.user.role === 'admin') {
        return next({ name: 'admin-dashboard' });
      }
      return next({ name: 'dashboard' });
    }
  }

  // Redirect authenticated users away from guest-only pages
  if (to.meta.guestOnly && authStore.isAuthenticated && authStore.user) {
    if (authStore.user.role === 'super_admin' || authStore.user.role === 'admin') {
      return next({ name: 'admin-dashboard' });
    }
    return next({ name: 'dashboard' });
  }

  next();
});

export default router;
